import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Flex, Container, Hide } from 'pcln-design-system'
import buildMonths from './helpers/buildMonths'
import Month from './Month'
import CalendarCard from './CalendarCard'
import Footer from './Footer.js'
import { parseISO, format, addDays, differenceInMonths } from 'date-fns'
import deprecatedPropType from './helpers/deprecatedPropType/deprecatedPropType'
import { locales, validateLocale } from './helpers/locales.js'
import { calendarRootWillReceiveProps } from './helpers/calendarRootWillReceiveProps'

const now = Date.now()
export const defaultToday = format(now, 'yyyyMMdd')
export const defaultMaxDate = format(addDays(now, 330), 'yyyyMMdd')
const stringOrNumber = PropTypes.oneOfType([PropTypes.string, PropTypes.number])
const LeftAlignedContainer = styled(Container)`
  margin-left: unset;
  margin-right: unset;
  z-index: 4;
`
const CalendarFlex = styled(Flex)`
  position: relative;
  z-index: 4;
  ${(props) => props.theme.mediaQueries[1]} {
    box-shadow: ${(props) => (props.boxShadow ? props.theme.boxShadows[3] : 'none')};
  }
`

class CalendarRoot extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    animateScroll: PropTypes.bool,
    boxShadow: PropTypes.bool,
    startDate: stringOrNumber,
    endDate: stringOrNumber,
    scrollDate: stringOrNumber,
    minDate: PropTypes.string,
    maxDate: PropTypes.string,
    maxSelectableDate: PropTypes.string,
    calendarWrapperStyles: PropTypes.object,
    highlightedDates: PropTypes.array,
    monthsToShow: PropTypes.number,
    displayYear: PropTypes.bool,
    startsWithSunday: deprecatedPropType('locale'),
    locale: PropTypes.string,
    showHoveredDates: PropTypes.bool,
    setCalendarRef: PropTypes.func,
    onDayClicked: PropTypes.func,
    onDayHovered: PropTypes.func,
    dayWrapperStyleOverride: PropTypes.object,
    monthWidth: PropTypes.number,
    onDisplayMonthsChange: PropTypes.func,
    footer: PropTypes.node,
    setSelectedDayRef: PropTypes.func,
    onDismiss: PropTypes.func,
    hoveredDate: PropTypes.string,
    didUseKeyNavigation: PropTypes.bool,
    keyNavHandler: PropTypes.func,
    useDefaultFooter: PropTypes.bool,
  }

  static defaultProps = {
    minDate: defaultToday,
    maxDate: defaultMaxDate,
    boxShadow: true,
    monthsToShow: 2,
    displayYear: true,
    startsWithSunday: true,
    showHoveredDates: true,
    monthWidth: 380,
    maxSelectableDate: defaultMaxDate,
    onDayClicked: () => {},
    onDayHovered: () => {},
    onDisplayMonthsChange: () => {},
  }

  constructor(props) {
    super(props)

    const { minDate, maxDate, startsWithSunday, locale } = props
    // locale is preferred but startsWithSunday will override.
    let weekStartsOn = startsWithSunday ? 0 : 1
    if (locale !== undefined) {
      const validLocale = validateLocale(locale)
      // eslint-disable-next-line no-unused-vars
      weekStartsOn = (locales[validLocale].options && locales[validLocale].options.weekStartsOn) || 0
    }

    this.state = {
      months: buildMonths(minDate, maxDate, startsWithSunday, locale),
    }

    const firstDayOfMonth = new Date(this.state.months[0].year, this.state.months[0].monthIndex, 1)
    this.firstValidMonth = format(firstDayOfMonth, 'yyyyMM')
    this.isTouch = document ? 'ontouchstart' in document.documentElement : true
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const nextState = calendarRootWillReceiveProps(this.props, nextProps)

    if (nextState) {
      this.setState(nextState)
    }
  }

  render() {
    const {
      className,
      animateScroll,
      boxShadow,
      calendarWrapperStyles,
      highlightedDates,
      startDate,
      endDate,
      scrollDate,
      minDate,
      maxDate,
      maxSelectableDate,
      monthsToShow,
      displayYear,
      hoveredDate,
      startsWithSunday,
      showHoveredDates,
      setCalendarRef,
      onDayClicked,
      onDayHovered,
      dayWrapperStyleOverride,
      monthWidth,
      onDisplayMonthsChange,
      footer,
      didUseKeyNavigation,
      keyNavHandler,
      onDismiss,
      useDefaultFooter,
      locale,
    } = this.props

    const scrollMonth = scrollDate || startDate
    const scrollToMonth = scrollMonth
      ? differenceInMonths(parseISO(scrollMonth, 'yyyyMMdd'), parseISO(minDate, 'yyyyMMdd'))
      : 0

    return (
      <LeftAlignedContainer className={className} maxWidth={monthWidth * monthsToShow}>
        <CalendarFlex flexDirection='column-reverse' boxShadow={boxShadow && footer ? boxShadow : null}>
          <Hide xs sm>
            <Flex width={[monthsToShow * monthWidth]}>
              {footer &&
                !useDefaultFooter &&
                React.cloneElement(footer, {
                  dismissCalendar: onDismiss || undefined,
                })}

              {useDefaultFooter && <Footer dismissCalendar={onDismiss} />}
            </Flex>
          </Hide>
          <CalendarCard
            boxShadow={boxShadow && !footer ? boxShadow : null}
            monthsToShow={monthsToShow}
            monthWidth={monthWidth}
            calendarWrapperStyles={calendarWrapperStyles}
            scrollToMonth={scrollToMonth}
            firstValidMonth={this.firstValidMonth}
            setCalendarRef={setCalendarRef}
            onDisplayMonthsChange={onDisplayMonthsChange}
            keyNavHandler={keyNavHandler}
            hoveredDate={hoveredDate}
            maxSelectableDate={maxSelectableDate}
            animateScroll={animateScroll}
          >
            {this.state.months.map((month, i) => (
              <Month
                {...month}
                endDate={endDate}
                hoveredDate={hoveredDate}
                highlightedDates={highlightedDates}
                maxDate={maxDate}
                onDayClicked={onDayClicked}
                onDayHovered={onDayHovered}
                startDate={startDate}
                startsWithSunday={startsWithSunday}
                locale={locale}
                minDate={minDate}
                maxSelectableDate={maxSelectableDate}
                dayWrapperStyleOverride={dayWrapperStyleOverride}
                showHoveredDates={showHoveredDates}
                didUseKeyNavigation={didUseKeyNavigation}
                monthWidth={monthWidth}
                displayYear={displayYear}
                isTouch={this.isTouch}
                setSelectedDayRef={this.props.setSelectedDayRef}
                key={i}
              />
            ))}
          </CalendarCard>
        </CalendarFlex>
      </LeftAlignedContainer>
    )
  }
}

export default CalendarRoot
